// ============================================================
// Génération de minerais et régénération automatique (WorldOriginMod) : modèle de la page
// ============================================================
// Le fichier : data/mods/worldorigin.config.json de l'image, section Levels (un bloc par monde, désigné
// par ses Servers). La page ne lit et n'écrit que deux clés de chaque bloc : Ores (génération de
// minerais) et AutoRegen (régénération à date fixe). Tout le reste du bloc (origine, bornes, rollback...)
// repart tel qu'il a été lu, et l'API, quand elle connaît WorldOriginMod, ne prend de toute façon que ces
// deux clés (WorldOriginOres.java).
//
// Les valeurs par défaut et les bornes sont celles de WorldOriginMod 0.0.3 (Config/OreGenSettings.cs,
// Config/AutoRegenSettings.cs) : une valeur que le mod corrigerait au démarrage est signalée ici, et
// l'enregistrement est bloqué tant qu'elle reste. Les objets lus sont gardés tels quels (clés inconnues
// comprises) : une modification ne touche que le champ modifié.
// ============================================================

import {
  WORLD_CEILING_Y, WORLD_FLOOR_Y, MAX_VEIN_SIZE, clamp, clampY, parseDist, parseShape, sealConfig, toSeed,
  type GenConfig, type GenLayer, type GenOre, type OreDist, type OreShape,
} from './oreGenerator.ts';

export type Json = null | boolean | number | string | Json[] | { [key: string]: Json };
export type JsonObject = { [key: string]: Json };

export const isObject = (v: unknown): v is JsonObject => typeof v === 'object' && v !== null && !Array.isArray(v);

// ==================== Lecture avec les défauts du mod ====================

export interface OreLine {
  name: string;
  block: string;
  javaId: string;
  active: boolean;
  attempts: number;
  sizeMin: number;
  /** null : SizeMin (le champ est absent du fichier). */
  sizeMax: number | null;
  dist: OreDist;
  plateau: number;
  /** null : la plage de la couche. */
  minY: number | null;
  maxY: number | null;
  shape: OreShape;
  scatterSpread: number;
}

export interface OreLayer {
  name: string;
  minY: number;
  maxY: number;
  fillBlock: string;
  ores: OreLine[];
}

export interface ClusterType {
  block: string;
  weight: number;
  minY: number;
  maxY: number;
}

export interface ClusterSettings {
  enabled: boolean;
  perChunk: number[];
  cover: number;
  faces: string[];
  supports: string[];
  types: ClusterType[];
}

export interface OresView {
  enabled: boolean;
  seed: bigint;
  newDrawEachRegen: boolean;
  hostBlocks: string[];
  deepslateVariants: boolean;
  purgeBlocks: string[];
  purgeFillBlock: string;
  layers: OreLayer[];
  clusters: ClusterSettings;
}

export interface AutoRegenView {
  enabled: boolean;
  every: string;
  anchor: string;
  dayOfWeek: string;
  time: string;
  minLeadHours: number;
  announceBefore: string[];
  sendPlayersTo: string;
  bypassPermission: string;
  welcomeMinutes: number;
  displayName: string;
  chunksPerSecond: number;
  threads: number;
  minTps: number;
  spareActiveRegions: boolean;
  endVillagesBefore: boolean;
}

export const ALL_FACES = ['up', 'down', 'north', 'south', 'west', 'east'] as const;

const num = (v: unknown, fallback: number) => (typeof v === 'number' && Number.isFinite(v) ? v : fallback);
const int = (v: unknown, fallback: number) => (typeof v === 'number' && Number.isFinite(v) ? Math.trunc(v) : fallback);
const str = (v: unknown, fallback: string) => (typeof v === 'string' ? v : fallback);
const bool = (v: unknown, fallback: boolean) => (typeof v === 'boolean' ? v : fallback);
const optInt = (v: unknown) => (typeof v === 'number' && Number.isFinite(v) ? Math.trunc(v) : null);
const strings = (v: unknown) => (Array.isArray(v) ? v.filter((s): s is string => typeof s === 'string') : []);

export function readLine(raw: unknown): OreLine {
  const o = isObject(raw) ? raw : {};
  return {
    name: str(o.Name, ''),
    block: str(o.Block, '').trim(),
    javaId: str(o.JavaId, ''),
    active: bool(o.Active, true),
    attempts: num(o.Attempts, 0),
    sizeMin: int(o.SizeMin, 8),
    sizeMax: optInt(o.SizeMax),
    dist: parseDist(o.Dist),
    plateau: int(o.Plateau, 0),
    minY: optInt(o.MinY),
    maxY: optInt(o.MaxY),
    shape: parseShape(o.Shape),
    scatterSpread: int(o.ScatterSpread, 6),
  };
}

export function readLayer(raw: unknown): OreLayer {
  const o = isObject(raw) ? raw : {};
  return {
    name: str(o.Name, ''),
    minY: int(o.MinY, WORLD_FLOOR_Y),
    maxY: int(o.MaxY, WORLD_CEILING_Y),
    fillBlock: str(o.FillBlock, 'minecraft:stone') || 'minecraft:stone',
    ores: Array.isArray(o.Ores) ? o.Ores.map(readLine) : [],
  };
}

export function readClusters(raw: unknown): ClusterSettings {
  const o = isObject(raw) ? raw : {};
  return {
    enabled: bool(o.Enabled, false),
    perChunk: Array.isArray(o.PerChunk) ? o.PerChunk.map((p) => num(p, 0)) : [0.15, 0.35, 0.3, 0.2],
    cover: int(o.Cover, 6),
    faces: Array.isArray(o.Faces) ? strings(o.Faces).map((f) => f.trim().toLowerCase()) : [...ALL_FACES],
    supports: strings(o.Supports),
    types: Array.isArray(o.Types)
      ? o.Types.map((t) => {
        const c = isObject(t) ? t : {};
        return { block: str(c.Block, '').trim(), weight: int(c.Weight, 1), minY: int(c.MinY, WORLD_FLOOR_Y), maxY: int(c.MaxY, WORLD_CEILING_Y) };
      })
      : [],
  };
}

export function readOres(raw: unknown): OresView {
  const o = isObject(raw) ? raw : {};
  return {
    enabled: bool(o.Enabled, false),
    seed: toSeed(o.Seed ?? 0),
    newDrawEachRegen: bool(o.NewDrawEachRegen, true),
    hostBlocks: strings(o.HostBlocks),
    deepslateVariants: bool(o.DeepslateVariants, true),
    purgeBlocks: strings(o.PurgeBlocks),
    purgeFillBlock: str(o.PurgeFillBlock, 'minecraft:stone') || 'minecraft:stone',
    layers: Array.isArray(o.Layers) ? o.Layers.map(readLayer) : [],
    clusters: readClusters(o.Clusters),
  };
}

export function readAutoRegen(raw: unknown): AutoRegenView {
  const o = isObject(raw) ? raw : {};
  return {
    enabled: bool(o.Enabled, false),
    every: str(o.Every, '14d'),
    anchor: str(o.Anchor, 'auto') || 'auto',
    dayOfWeek: str(o.DayOfWeek, 'Monday'),
    time: str(o.Time, '05:00'),
    minLeadHours: int(o.MinLeadHours, 24),
    announceBefore: Array.isArray(o.AnnounceBefore) ? strings(o.AnnounceBefore) : ['24h', '1h', '10m', '1m'],
    sendPlayersTo: str(o.SendPlayersTo, 'capital'),
    bypassPermission: str(o.BypassPermission, ''),
    welcomeMinutes: int(o.WelcomeMinutes, 60),
    displayName: str(o.DisplayName, ''),
    chunksPerSecond: num(o.ChunksPerSecond, 0),
    threads: int(o.Threads, 4),
    minTps: num(o.MinTps, 0),
    spareActiveRegions: bool(o.SpareActiveRegions, true),
    endVillagesBefore: bool(o.EndVillagesBefore, true),
  };
}

// ==================== Pour le générateur ====================

/**
 * La config du générateur, comme WorldOriginMod la construit (OreGenSettings.ToGenConfig après
 * Normalize) : bornes appliquées, plage d'une ligne croisée avec celle de sa couche, ligne inactive
 * gardée à son rang.
 */
export function toGenConfig(ores: OresView, seed: bigint): GenConfig {
  const layers: GenLayer[] = ores.layers.map((layerView, layerIndex) => {
    let lMin = clampY(layerView.minY);
    let lMax = clampY(layerView.maxY);
    if (lMin > lMax) [lMin, lMax] = [lMax, lMin];
    const lines = layerView.ores.map((line, oreIndex): GenOre => {
      const sizeMin = clamp(line.sizeMin, 1, MAX_VEIN_SIZE);
      const sizeMax = line.sizeMax === null ? sizeMin : clamp(line.sizeMax, sizeMin, MAX_VEIN_SIZE);
      const minY = Math.max(lMin, clampY(line.minY ?? lMin));
      const maxY = Math.min(lMax, clampY(line.maxY ?? lMax));
      const attempts = clamp(line.attempts, 0, 4096);
      const block = line.active ? line.block : '';
      return {
        layerIndex,
        oreIndex,
        name: line.name.trim() || `minerai${oreIndex}`,
        block,
        meta: 0,
        attempts,
        sizeMin,
        sizeMax,
        dist: line.dist,
        plateau: line.dist === 'triangle' ? 0 : clamp(Math.max(0, line.plateau), 0, Math.max(0, maxY - minY)),
        minY,
        maxY,
        shape: line.shape,
        scatterSpread: clamp(line.scatterSpread, 1, 32),
        active: attempts > 0 && minY <= maxY && block.length > 0,
      };
    });
    return { name: layerView.name, minY: lMin, maxY: lMax, fill: layerView.fillBlock, ores: lines };
  });
  return sealConfig(seed, layers);
}

// ==================== Contrôles (ceux du mod et de l'API du panel) ====================

export interface Problem {
  /** Chemin dans le bloc du monde : Ores.Layers[1].Ores[7].SizeMax, AutoRegen.Anchor... */
  path: string;
  message: string;
}

const BLOCK_ID = /^[a-z0-9_.-]+:[a-z0-9_./-]+$/;
/** Identifiant, ou préfixe terminé par * (ng:ore_cluster_*, propsi:*), comme l'API du panel. */
const BLOCK_PATTERN = /^[a-z0-9_.-]+:(?:[a-z0-9_./-]+|[a-z0-9_./-]*\*)$/;
/** Texte d'au plus 200 caractères (règle de l'API du panel). */
const MAX_TEXT = 200;
const MAX_IDS = 256;
const DURATION = /^\s*(\d+(?:[.,]\d+)?)\s*(d|j|h|m|min|s)\s*$/i;
const ANCHOR = /^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})(?::(\d{2}))?$/;
const TIME = /^(\d{1,2}):(\d{2})$/;

export const isBlockId = (v: string) => BLOCK_ID.test(v);

/** Durée courte du mod (14d, 24h, 10m, 30s) en millisecondes, ou null. */
export function parseDuration(value: string): number | null {
  const m = DURATION.exec(value ?? '');
  if (!m) return null;
  const amount = Number(m[1].replace(',', '.'));
  const unit = m[2].toLowerCase();
  const factor = unit === 'd' || unit === 'j' ? 86_400_000 : unit === 'h' ? 3_600_000 : unit === 'm' || unit === 'min' ? 60_000 : 1000;
  return amount * factor;
}

const DAYS: Record<string, number> = {
  sunday: 0, dimanche: 0, monday: 1, lundi: 1, tuesday: 2, mardi: 2, wednesday: 3, mercredi: 3,
  thursday: 4, jeudi: 4, friday: 5, vendredi: 5, saturday: 6, samedi: 6,
};

/** Jour de la semaine (0 = dimanche), en anglais ou en français, ou null. */
export const parseDay = (value: string): number | null => DAYS[(value ?? '').trim().toLowerCase()] ?? null;

/** Heure HH:mm en minutes depuis minuit, ou null. */
export function parseTime(value: string): number | null {
  const m = TIME.exec((value ?? '').trim());
  if (!m) return null;
  const h = Number(m[1]);
  const min = Number(m[2]);
  return h < 24 && min < 60 ? h * 60 + min : null;
}

export interface LocalDateTime { year: number; month: number; day: number; hour: number; minute: number }

/** Ancre fixe AAAA-MM-JJTHH:mm (heure locale du fuseau du monde), ou null. */
export function parseAnchor(value: string): LocalDateTime | null {
  const m = ANCHOR.exec((value ?? '').trim());
  if (!m) return null;
  const [year, month, day, hour, minute] = [Number(m[1]), Number(m[2]), Number(m[3]), Number(m[4]), Number(m[5])];
  const check = new Date(Date.UTC(year, month - 1, day, hour, minute));
  if (check.getUTCFullYear() !== year || check.getUTCMonth() !== month - 1 || check.getUTCDate() !== day
    || hour > 23 || minute > 59) return null;
  return { year, month, day, hour, minute };
}

const isInt = (v: Json | undefined) => typeof v === 'number' && Number.isInteger(v);
const blockOrEmpty = (v: string) => v.trim() === '' || BLOCK_ID.test(v.trim());

/** Texte facultatif : une chaîne de 200 caractères au plus (null refusé, comme l'API du panel). */
function textProblem(v: Json | undefined, path: string, add: (path: string, message: string) => void): void {
  if (v === undefined) return;
  if (typeof v !== 'string') add(path, 'texte attendu');
  else if (v.length > MAX_TEXT) add(path, `${MAX_TEXT} caractères au plus`);
}
const isNum = (v: Json | undefined) => typeof v === 'number' && Number.isFinite(v);

/** Ce que le mod refuserait ou corrigerait dans le bloc Ores d'un monde (chemins relatifs au bloc du monde). */
export function oresProblems(raw: Json | undefined): Problem[] {
  const out: Problem[] = [];
  if (raw === undefined || raw === null) return out;
  const add = (path: string, message: string) => out.push({ path: `Ores${path}`, message });
  if (!isObject(raw)) { add('', 'objet attendu'); return out; }
  const flag = (key: string) => { if (raw[key] !== undefined && typeof raw[key] !== 'boolean') add(`.${key}`, 'vrai ou faux attendu'); };
  flag('Enabled'); flag('NewDrawEachRegen'); flag('DeepslateVariants');
  // Le mod lie un long : un nombre entier, jamais une chaîne. Au-delà de 2^53, le navigateur l'arrondirait.
  if (raw.Seed !== undefined && !(isInt(raw.Seed) && Number.isSafeInteger(raw.Seed as number))) {
    add('.Seed', 'entier attendu (0 : la graine du monde), au plus 9 007 199 254 740 991 en valeur absolue');
  }
  const ids = (key: string, allowPattern: boolean) => {
    const v = raw[key];
    if (v === undefined) return;
    if (!Array.isArray(v)) { add(`.${key}`, 'liste attendue'); return; }
    if (v.length > MAX_IDS) add(`.${key}`, `au plus ${MAX_IDS} identifiants`);
    v.forEach((id, i) => {
      if (typeof id !== 'string' || !(allowPattern ? BLOCK_PATTERN : BLOCK_ID).test(id.trim())) {
        add(`.${key}[${i}]`, `« ${String(id)} » n'est pas un identifiant de bloc (espace:nom en minuscules${allowPattern ? ', * final permis' : ''})`);
      }
    });
  };
  ids('HostBlocks', false);
  ids('PurgeBlocks', true);
  if (raw.PurgeFillBlock !== undefined && (typeof raw.PurgeFillBlock !== 'string' || !blockOrEmpty(raw.PurgeFillBlock))) {
    add('.PurgeFillBlock', 'identifiant de bloc attendu (vide : minecraft:stone)');
  }
  const layers = raw.Layers;
  if (layers !== undefined) {
    if (!Array.isArray(layers)) add('.Layers', 'liste attendue');
    else {
      if (layers.length > 16) add('.Layers', 'au plus 16 couches');
      layers.forEach((layer, li) => {
        const lp = `.Layers[${li}]`;
        if (!isObject(layer)) { add(lp, 'objet attendu'); return; }
        for (const key of ['MinY', 'MaxY']) {
          const v = layer[key];
          if (v !== undefined && !(isInt(v) && (v as number) >= WORLD_FLOOR_Y && (v as number) <= WORLD_CEILING_Y)) {
            add(`${lp}.${key}`, `entier de ${WORLD_FLOOR_Y} à ${WORLD_CEILING_Y} attendu`);
          }
        }
        if (isInt(layer.MinY) && isInt(layer.MaxY) && (layer.MinY as number) > (layer.MaxY as number)) {
          add(`${lp}.MinY`, 'le bas de la couche est au-dessus de son haut');
        }
        if (layer.FillBlock !== undefined && (typeof layer.FillBlock !== 'string' || !blockOrEmpty(layer.FillBlock))) {
          add(`${lp}.FillBlock`, 'identifiant de bloc attendu (vide : minecraft:stone)');
        }
        textProblem(layer.Name, `${lp}.Name`, add);
        const lines = layer.Ores;
        if (lines === undefined) return;
        if (!Array.isArray(lines)) { add(`${lp}.Ores`, 'liste attendue'); return; }
        if (lines.length > 64) add(`${lp}.Ores`, 'au plus 64 lignes par couche');
        lines.forEach((line, oi) => lineProblems(line, `${lp}.Ores[${oi}]`, add));
      });
    }
  }
  clusterProblems(raw.Clusters, add);
  return out;
}

function lineProblems(line: Json, p: string, add: (path: string, message: string) => void): void {
  if (!isObject(line)) { add(p, 'objet attendu'); return; }
  textProblem(line.Name, `${p}.Name`, add);
  if (line.Active !== undefined && typeof line.Active !== 'boolean') add(`${p}.Active`, 'vrai ou faux attendu');
  const active = line.Active !== false;
  if (line.Block !== undefined && typeof line.Block !== 'string') add(`${p}.Block`, 'texte attendu');
  else {
    // Bloc obligatoire pour une ligne active ; une ligne coupée peut le laisser vide, pas le mal écrire.
    const block = String(line.Block ?? '').trim();
    if (block === '' ? active : !BLOCK_ID.test(block)) {
      add(`${p}.Block`, block === '' ? 'bloc obligatoire pour une ligne active' : `« ${block} » n'est pas un identifiant de bloc (espace:nom en minuscules)`);
    }
  }
  if (line.Attempts !== undefined && !(isNum(line.Attempts) && (line.Attempts as number) >= 0 && (line.Attempts as number) <= 4096)) {
    add(`${p}.Attempts`, 'nombre de 0 à 4096 attendu');
  }
  const sizeMin = line.SizeMin === undefined ? 8 : line.SizeMin;
  if (!(isInt(sizeMin) && (sizeMin as number) >= 1 && (sizeMin as number) <= MAX_VEIN_SIZE)) {
    add(`${p}.SizeMin`, `entier de 1 à ${MAX_VEIN_SIZE} attendu`);
  }
  if (line.SizeMax !== undefined && line.SizeMax !== null) {
    if (!(isInt(line.SizeMax) && (line.SizeMax as number) <= MAX_VEIN_SIZE
      && (!isInt(sizeMin) || (line.SizeMax as number) >= (sizeMin as number)))) {
      add(`${p}.SizeMax`, `entier de la taille minimale à ${MAX_VEIN_SIZE} attendu (vide : la taille minimale)`);
    }
  }
  if (line.Dist !== undefined && !(typeof line.Dist === 'string' && ['uniform', 'triangle', 'trapezoid', 'trapeze'].includes(line.Dist.trim().toLowerCase()))) {
    add(`${p}.Dist`, 'uniform, triangle ou trapezoid attendu');
  }
  if (line.Shape !== undefined && !(typeof line.Shape === 'string' && ['blob', 'scatter'].includes(line.Shape.trim().toLowerCase()))) {
    add(`${p}.Shape`, 'blob ou scatter attendu');
  }
  if (line.Plateau !== undefined && !(isInt(line.Plateau) && (line.Plateau as number) >= 0)) add(`${p}.Plateau`, 'entier positif attendu');
  if (line.ScatterSpread !== undefined && !(isInt(line.ScatterSpread) && (line.ScatterSpread as number) >= 1 && (line.ScatterSpread as number) <= 32)) {
    add(`${p}.ScatterSpread`, 'entier de 1 à 32 attendu');
  }
  for (const key of ['MinY', 'MaxY']) {
    const v = line[key];
    if (v !== undefined && v !== null && !(isInt(v) && (v as number) >= WORLD_FLOOR_Y && (v as number) <= WORLD_CEILING_Y)) {
      add(`${p}.${key}`, `entier de ${WORLD_FLOOR_Y} à ${WORLD_CEILING_Y} attendu (vide : la couche)`);
    }
  }
  if (line.JavaId !== undefined && line.JavaId !== null) textProblem(line.JavaId, `${p}.JavaId`, add);
}

function clusterProblems(raw: Json | undefined, add: (path: string, message: string) => void): void {
  if (raw === undefined) return;
  const p = '.Clusters';
  if (!isObject(raw)) { add(p, 'objet attendu'); return; }
  if (raw.Enabled !== undefined && typeof raw.Enabled !== 'boolean') add(`${p}.Enabled`, 'vrai ou faux attendu');
  if (raw.PerChunk !== undefined) {
    if (!Array.isArray(raw.PerChunk) || raw.PerChunk.length > 16 || raw.PerChunk.some((v) => !isNum(v) || (v as number) < 0)) {
      add(`${p}.PerChunk`, 'au plus 16 probabilités positives attendues');
    } else {
      const total = (raw.PerChunk as number[]).reduce((a, b) => a + b, 0);
      if (total > 1 + 1e-6) add(`${p}.PerChunk`, `la somme des probabilités vaut ${Math.round(total * 1000) / 10} %, au plus 100 %`);
    }
  }
  if (raw.Cover !== undefined && !(isInt(raw.Cover) && (raw.Cover as number) >= 0 && (raw.Cover as number) <= 64)) add(`${p}.Cover`, 'entier de 0 à 64 attendu');
  if (raw.Faces !== undefined) {
    if (!Array.isArray(raw.Faces)) add(`${p}.Faces`, 'liste attendue');
    else raw.Faces.forEach((f, i) => {
      if (typeof f !== 'string' || !(ALL_FACES as readonly string[]).includes(f.trim().toLowerCase())) {
        add(`${p}.Faces[${i}]`, 'up, down, north, south, west ou east attendu');
      }
    });
  }
  if (raw.Supports !== undefined) {
    if (!Array.isArray(raw.Supports)) add(`${p}.Supports`, 'liste attendue');
    else if (raw.Supports.length > MAX_IDS) add(`${p}.Supports`, `au plus ${MAX_IDS} identifiants`);
    else raw.Supports.forEach((id, i) => {
      if (typeof id !== 'string' || !BLOCK_ID.test(id.trim())) add(`${p}.Supports[${i}]`, 'identifiant de bloc attendu');
    });
  }
  if (raw.Types !== undefined) {
    if (!Array.isArray(raw.Types)) { add(`${p}.Types`, 'liste attendue'); return; }
    if (raw.Types.length > 64) add(`${p}.Types`, "au plus 64 sortes d'amas");
    raw.Types.forEach((t, i) => {
      const tp = `${p}.Types[${i}]`;
      if (!isObject(t)) { add(tp, 'objet attendu'); return; }
      if (typeof t.Block !== 'string' || !BLOCK_ID.test(t.Block.trim())) add(`${tp}.Block`, 'identifiant de bloc attendu');
      if (t.Weight !== undefined && !(isInt(t.Weight) && (t.Weight as number) >= 1)) add(`${tp}.Weight`, 'entier de 1 ou plus attendu');
      for (const key of ['MinY', 'MaxY']) {
        const v = t[key];
        if (v !== undefined && !(isInt(v) && (v as number) >= WORLD_FLOOR_Y && (v as number) <= WORLD_CEILING_Y)) {
          add(`${tp}.${key}`, `entier de ${WORLD_FLOOR_Y} à ${WORLD_CEILING_Y} attendu`);
        }
      }
      if (isInt(t.MinY) && isInt(t.MaxY) && (t.MinY as number) > (t.MaxY as number)) add(`${tp}.MinY`, 'le bas de la bande est au-dessus de son haut');
    });
  }
}

/** Ce que le mod refuserait ou corrigerait dans le bloc AutoRegen d'un monde. */
export function autoRegenProblems(raw: Json | undefined): Problem[] {
  const out: Problem[] = [];
  if (raw === undefined || raw === null) return out;
  const add = (path: string, message: string) => out.push({ path: `AutoRegen${path}`, message });
  if (!isObject(raw)) { add('', 'objet attendu'); return out; }
  for (const key of ['Enabled', 'SpareActiveRegions', 'EndVillagesBefore']) {
    if (raw[key] !== undefined && typeof raw[key] !== 'boolean') add(`.${key}`, 'vrai ou faux attendu');
  }
  for (const key of ['SendPlayersTo', 'BypassPermission', 'DisplayName']) textProblem(raw[key], `AutoRegen.${key}`, (path, message) => out.push({ path, message }));
  if (raw.Every !== undefined) {
    const every = typeof raw.Every === 'string' ? parseDuration(raw.Every) : null;
    if (every === null || every < 60_000) add('.Every', 'durée d\'au moins 1 minute attendue (14d, 12h, 30m)');
  }
  if (raw.Anchor !== undefined) {
    const anchor = typeof raw.Anchor === 'string' ? raw.Anchor.trim() : '';
    if (anchor.toLowerCase() !== 'auto' && anchor !== '' && parseAnchor(anchor) === null) add('.Anchor', 'auto, ou une date AAAA-MM-JJTHH:mm attendue');
    if (typeof raw.Anchor !== 'string') add('.Anchor', 'texte attendu');
  }
  if (raw.DayOfWeek !== undefined && (typeof raw.DayOfWeek !== 'string' || parseDay(raw.DayOfWeek) === null)) add('.DayOfWeek', 'jour de la semaine attendu (Monday ou lundi...)');
  if (raw.Time !== undefined && (typeof raw.Time !== 'string' || parseTime(raw.Time) === null)) add('.Time', 'heure HH:mm attendue');
  const ranged = (key: string, min: number, max: number, integer: boolean) => {
    const v = raw[key];
    if (v === undefined) return;
    if (!(integer ? isInt(v) : isNum(v)) || (v as number) < min || (v as number) > max) {
      add(`.${key}`, `${integer ? 'entier' : 'nombre'} de ${min} à ${max} attendu`);
    }
  };
  ranged('MinLeadHours', 0, 720, true);
  ranged('WelcomeMinutes', 0, 1440, true);
  ranged('ChunksPerSecond', 0, 100_000, false);
  ranged('Threads', 1, 32, true);
  ranged('MinTps', 0, 20, false);
  if (raw.AnnounceBefore !== undefined) {
    if (!Array.isArray(raw.AnnounceBefore)) add('.AnnounceBefore', 'liste attendue');
    else if (raw.AnnounceBefore.length > 16) add('.AnnounceBefore', 'au plus 16 annonces');
    else raw.AnnounceBefore.forEach((v, i) => {
      const d = typeof v === 'string' ? parseDuration(v) : null;
      if (d === null || d <= 0) add(`.AnnounceBefore[${i}]`, 'durée attendue (24h, 1h, 10m, 1m)');
    });
  }
  return out;
}

/** Tout ce qui bloque l'enregistrement d'un monde. */
export const levelProblems = (level: JsonObject): Problem[] => [...oresProblems(level.Ores), ...autoRegenProblems(level.AutoRegen)];

// ==================== Mondes du fichier ====================

export interface WorldEntry {
  index: number;
  /** Nom affiché : Level, sinon le premier motif de Servers. */
  label: string;
  servers: string[];
  level: string;
  timeZone: string;
  hasOres: boolean;
  oresOn: boolean;
  hasAuto: boolean;
  autoOn: boolean;
}

export function listWorlds(levels: JsonObject[]): WorldEntry[] {
  return levels.map((level, index) => {
    const servers = strings(level.Servers);
    const levelName = str(level.Level, '');
    const base = servers.find((s) => !s.includes('*')) ?? servers[0] ?? `monde ${index + 1}`;
    return {
      index,
      label: levelName || base,
      servers,
      level: levelName,
      timeZone: str(level.TimeZone, 'Europe/Paris') || 'Europe/Paris',
      hasOres: isObject(level.Ores),
      oresOn: isObject(level.Ores) && level.Ores.Enabled === true,
      hasAuto: isObject(level.AutoRegen),
      autoOn: isObject(level.AutoRegen) && level.AutoRegen.Enabled === true,
    };
  });
}

/**
 * Levels à envoyer : ceux du fichier, où seules les clés Ores et AutoRegen des mondes modifiés sont
 * remplacées (ou retirées). Tout le reste vient du fichier tel qu'il a été lu.
 */
export function buildLevels(loaded: JsonObject[], edits: Record<number, { Ores?: Json; AutoRegen?: Json }>): JsonObject[] {
  return loaded.map((level, index) => {
    const edit = edits[index];
    if (!edit) return level;
    const out: JsonObject = { ...level };
    for (const key of ['Ores', 'AutoRegen'] as const) {
      if (!(key in edit)) continue;
      const value = edit[key];
      if (value === undefined) delete out[key];
      else out[key] = value;
    }
    return out;
  });
}

// ==================== Valeurs neuves ====================

export const DEFAULT_PURGE = [
  'minecraft:coal_ore', 'minecraft:deepslate_coal_ore', 'minecraft:iron_ore', 'minecraft:deepslate_iron_ore',
  'minecraft:copper_ore', 'minecraft:deepslate_copper_ore', 'minecraft:gold_ore', 'minecraft:deepslate_gold_ore',
  'minecraft:redstone_ore', 'minecraft:deepslate_redstone_ore', 'minecraft:lit_redstone_ore', 'minecraft:lit_deepslate_redstone_ore',
  'minecraft:lapis_ore', 'minecraft:deepslate_lapis_ore', 'minecraft:diamond_ore', 'minecraft:deepslate_diamond_ore',
  'minecraft:emerald_ore', 'minecraft:deepslate_emerald_ore',
];

/** Bloc Ores d'un monde qui n'en a pas : éteint, sans couche. */
export const newOres = (): JsonObject => ({
  Enabled: false,
  Seed: 0,
  NewDrawEachRegen: true,
  HostBlocks: ['minecraft:stone', 'minecraft:deepslate'],
  DeepslateVariants: true,
  PurgeBlocks: [...DEFAULT_PURGE],
  PurgeFillBlock: 'minecraft:stone',
  Layers: [],
  Clusters: { Enabled: false, PerChunk: [0.15, 0.35, 0.3, 0.2], Cover: 6, Faces: [...ALL_FACES], Supports: [], Types: [] },
});

/** Bloc AutoRegen d'un monde qui n'en a pas : éteint, valeurs par défaut du mod. */
export const newAutoRegen = (): JsonObject => ({
  Enabled: false,
  Every: '14d',
  Anchor: 'auto',
  DayOfWeek: 'Monday',
  Time: '05:00',
  MinLeadHours: 24,
  AnnounceBefore: ['24h', '1h', '10m', '1m'],
  SendPlayersTo: 'capital',
  BypassPermission: '',
  WelcomeMinutes: 60,
  DisplayName: '',
  ChunksPerSecond: 0,
  Threads: 4,
  MinTps: 0,
  SpareActiveRegions: true,
  EndVillagesBefore: true,
});

export const newLayer = (): JsonObject => ({ Name: 'Nouvelle couche', MinY: 0, MaxY: 64, FillBlock: 'minecraft:stone', Ores: [] });

export const newLine = (): JsonObject => ({
  Name: 'Nouveau minerai', Block: '', Active: true, Attempts: 4, SizeMin: 6, SizeMax: 12, Dist: 'uniform', Shape: 'blob',
});

export const newClusterType = (): JsonObject => ({ Block: '', Weight: 1, MinY: WORLD_FLOOR_Y, MaxY: WORLD_CEILING_Y });

// ==================== Calendrier ====================

const pad = (n: number) => String(n).padStart(2, '0');

/** Décalage (ms) du fuseau à l'instant `utc` : heure locale moins heure UTC. */
function offsetAt(utc: number, timeZone: string): number {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone, hourCycle: 'h23', year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit',
  }).formatToParts(new Date(utc));
  const get = (type: string) => Number(parts.find((p) => p.type === type)?.value ?? 0);
  const asUtc = Date.UTC(get('year'), get('month') - 1, get('day'), get('hour') === 24 ? 0 : get('hour'), get('minute'), get('second'));
  return asUtc - Math.floor(utc / 1000) * 1000;
}

/** Heure locale (fuseau du monde) d'un instant UTC. */
export function toLocal(utc: number, timeZone: string): LocalDateTime & { weekday: number } {
  const local = new Date(utc + offsetAt(utc, timeZone));
  return {
    year: local.getUTCFullYear(), month: local.getUTCMonth() + 1, day: local.getUTCDate(),
    hour: local.getUTCHours(), minute: local.getUTCMinutes(), weekday: local.getUTCDay(),
  };
}

/**
 * Instant UTC d'une heure locale du fuseau. Une heure qui n'existe pas (passage à l'heure d'été) est
 * avancée d'une heure, comme le fait le mod (OccurrenceUtc).
 */
export function fromLocal(t: LocalDateTime, timeZone: string): number {
  const naive = Date.UTC(t.year, t.month - 1, t.day, t.hour, t.minute);
  let utc = naive - offsetAt(naive, timeZone);
  utc = naive - offsetAt(utc, timeZone);
  const back = toLocal(utc, timeZone);
  if (back.hour !== t.hour || back.minute !== t.minute || back.day !== t.day) {
    return fromLocal({ ...t, hour: t.hour + 1 }, timeZone);
  }
  return utc;
}

/** La date locale `days` jours plus tard, même heure locale (5 h reste 5 h aux changements d'heure). */
function addDays(t: LocalDateTime, days: number): LocalDateTime {
  const d = new Date(Date.UTC(t.year, t.month - 1, t.day + days, t.hour, t.minute));
  return { year: d.getUTCFullYear(), month: d.getUTCMonth() + 1, day: d.getUTCDate(), hour: d.getUTCHours(), minute: d.getUTCMinutes() };
}

function addMs(t: LocalDateTime, ms: number): LocalDateTime {
  const d = new Date(Date.UTC(t.year, t.month - 1, t.day, t.hour, t.minute) + ms);
  return { year: d.getUTCFullYear(), month: d.getUTCMonth() + 1, day: d.getUTCDate(), hour: d.getUTCHours(), minute: d.getUTCMinutes() };
}

export interface Schedule {
  /** Ancre utilisée : fixe, ou celle qu'un démarrage maintenant calculerait (auto). */
  anchor: LocalDateTime | null;
  auto: boolean;
  /** Prochaines dates (instants UTC), la première comprise. */
  next: number[];
  problem: string | null;
}

/**
 * Prochaines régénérations comme le mod les calcule (RegenService.Auto.cs) : une date tous les Every
 * à partir de l'ancre, en heure locale ; retard toléré : 6 h au plus (moitié de l'intervalle si plus
 * court). Avec l'ancre auto, la date dépend du premier démarrage du serveur avec la régénération
 * allumée : la page montre celle d'un démarrage maintenant.
 */
export function schedule(auto: AutoRegenView, timeZone: string, now: number, count = 4): Schedule {
  const period = parseDuration(auto.every);
  if (period === null || period < 60_000) return { anchor: null, auto: false, next: [], problem: 'intervalle illisible' };
  const isAuto = auto.anchor.trim().toLowerCase() === 'auto' || auto.anchor.trim() === '';
  let anchor: LocalDateTime | null;
  if (isAuto) {
    const day = parseDay(auto.dayOfWeek) ?? 1;
    const minutes = parseTime(auto.time) ?? 300;
    const nowLocal = toLocal(now, timeZone);
    const earliest = addMs(nowLocal, Math.max(0, auto.minLeadHours) * 3_600_000);
    let candidate: LocalDateTime = { year: nowLocal.year, month: nowLocal.month, day: nowLocal.day, hour: Math.floor(minutes / 60), minute: minutes % 60 };
    const later = (a: LocalDateTime, b: LocalDateTime) => Date.UTC(a.year, a.month - 1, a.day, a.hour, a.minute) >= Date.UTC(b.year, b.month - 1, b.day, b.hour, b.minute);
    for (let guard = 0; guard < 400; guard += 1) {
      const weekday = new Date(Date.UTC(candidate.year, candidate.month - 1, candidate.day)).getUTCDay();
      if (weekday === day && later(candidate, earliest)) break;
      candidate = addDays(candidate, 1);
    }
    anchor = candidate;
  } else {
    anchor = parseAnchor(auto.anchor);
    if (!anchor) return { anchor: null, auto: false, next: [], problem: 'ancre illisible' };
  }
  const grace = Math.min(6 * 3_600_000, period / 2);
  const next: number[] = [];
  const dayMs = 86_400_000;
  const stepLocal = (k: number) => (period % dayMs === 0 ? addDays(anchor!, (period / dayMs) * k) : addMs(anchor!, period * k));
  // Premier rang qui n'est pas plus en retard que la grâce.
  const anchorUtc = fromLocal(anchor, timeZone);
  let k = Math.max(0, Math.floor((now - grace - anchorUtc) / period) - 1);
  for (let guard = 0; guard < 100_000 && next.length < count; guard += 1, k += 1) {
    const at = fromLocal(stepLocal(k), timeZone);
    if (at + grace > now) next.push(at);
  }
  return { anchor, auto: isAuto, next, problem: null };
}

const WEEKDAYS = ['dimanche', 'lundi', 'mardi', 'mercredi', 'jeudi', 'vendredi', 'samedi'];

/** « lundi 09/11/2026 05:00 » dans le fuseau du monde. */
export function formatLocal(utc: number, timeZone: string): string {
  const t = toLocal(utc, timeZone);
  return `${WEEKDAYS[t.weekday]} ${pad(t.day)}/${pad(t.month)}/${t.year} ${pad(t.hour)}:${pad(t.minute)}`;
}

/** L'ancre au format du fichier, depuis une valeur de champ datetime-local (AAAA-MM-JJTHH:mm). */
export const anchorFromInput = (value: string) => (parseAnchor(value) ? value.slice(0, 16) : value);
